import { FC, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { AppDispatch, RootState } from '../../../store/store';
import { getUUID } from '../../../utils/UUID';
import { ImageMeta } from '../../../store/mediaAPI/media/dto/request/media-save-request';
import { MediaSaveResponseDto } from '../../../store/mediaAPI/media/dto/response/media-save-response';
import { saveMedia } from '../../../store/mediaAPI/media/reducers/media.slice';
import { VerificationStatusResponseDto } from '../../../store/landscapeAPI/verificationStatus/dto/response/verification-status-response.dto';
import { VerificationStatusSaveRequestDto } from '../../../store/landscapeAPI/verificationStatus/dto/request/verification-status-save-request.dto';
import { saveVerificationStatus } from '../../../store/landscapeAPI/verificationStatus/reducers/verification-status.slice';
import Spinner from '../../atoms/Spinner/Spinner';
import { ReportingDetailsVerificationStatusesAddTemplate } from '../../templates';
import { LocalesLanguagesConstants } from '../../../locales/locales';
import { PromiseSettledResult } from '../../../utils/types/types';

interface ReportingDetailsVerificationStatusesAddPageProps {}

const ReportingDetailsVerificationStatusesAddPage: FC<
  ReportingDetailsVerificationStatusesAddPageProps
> = () => {
  const [verificationStatus, setVerificationStatus] =
    useState<VerificationStatusResponseDto>({
      logoUrl: '',
      name: '',
      description: '',
      id: getUUID(),
      versionId: 0,
      createdByUserId: '',
      createdOnDateTimeUtc: '',
      language: '',
      published: true,
    });

  const dispatch = useDispatch<AppDispatch>();

  const navigate = useNavigate();

  const { status } = useSelector(
    (state: RootState) => state.verificationStatusSlice
  );

  const { status: mediaStatus } = useSelector(
    (state: RootState) => state.mediaSlice
  );

  const [imageMeta, setImageMeta] = useState<ImageMeta>();

  const handleOnChange = (
    verificationStatus: VerificationStatusResponseDto
  ) => {
    setVerificationStatus(verificationStatus);
  };

  const handleImageOnChange = (imageMeta: ImageMeta) => {
    setImageMeta(imageMeta);
  };

  const [selectedLanguage, setLanguage] = useState<LocalesLanguagesConstants>(
    LocalesLanguagesConstants.en
  );

  const [fromLanguage, setFromLanguage] = useState<LocalesLanguagesConstants>(
    LocalesLanguagesConstants.en
  );

  const [toLanguage, setToLanguage] = useState<LocalesLanguagesConstants>(
    LocalesLanguagesConstants.en
  );

  const generateAllTranslations: () => Promise<
    PromiseSettledResult<void>[]
  > = async () => {
    const rejectedResults: PromiseSettledResult<void>[] = [
      {
        status: 'rejected',
        reason: new Error(
          'Must save verification status before generating translation'
        ),
      },
    ];

    return Promise.reject(rejectedResults);
  };

  const generateTranslation = async () => {};

  const handleAddVerificationStatus = async (
    verificationStatus: VerificationStatusResponseDto
  ) => {
    let icon: MediaSaveResponseDto = {
      href: '',
    };

    if (
      verificationStatus.logoUrl &&
      verificationStatus.logoUrl !== '' &&
      imageMeta
    ) {
      const formData = new FormData();
      formData.append(
        'file',
        new Blob([verificationStatus.logoUrl], {
          type: imageMeta?.imageType ?? 'image/png',
        }),
        imageMeta?.imageSrc
      );
      icon = (await dispatch(saveMedia(formData)))
        .payload as MediaSaveResponseDto;
    }

    const data: VerificationStatusSaveRequestDto = {
      id: verificationStatus.id,
      name: verificationStatus.name,
      description: verificationStatus.description,
      logoUrl: icon.href,
      published: verificationStatus.published,
    };

    const response = await dispatch(
      saveVerificationStatus({ saveRequest: data, language: selectedLanguage })
    );
    const newVerificationStatus =
      response.payload as VerificationStatusResponseDto;

    navigate(
      `/reporting-details/verification-statuses/${newVerificationStatus.id}/edit`
    );
  };

  if (status === 'loading' || mediaStatus === 'loading') {
    return <Spinner />;
  }

  return (
    <ReportingDetailsVerificationStatusesAddTemplate
      data={verificationStatus}
      handleAddVerificationStatus={handleAddVerificationStatus}
      handleOnChange={handleOnChange}
      handleImageOnChange={handleImageOnChange}
      setLanguage={setLanguage}
      selectedLanguage={selectedLanguage}
      fromLanguage={fromLanguage}
      setFromLanguage={setFromLanguage}
      toLanguage={toLanguage}
      setToLanguage={setToLanguage}
      generateTranslation={generateTranslation}
      generateAllTranslations={generateAllTranslations}
    />
  );
};

export default ReportingDetailsVerificationStatusesAddPage;
